import React from 'react';
import {
  Box,
  Button,
  Icon,
  MobileDrawer,
  Popover,
  Typography,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { NODE_DATA_MODEL } from '../../../models/CertsModels';
import { useInvertedLocalize } from '../../../utils/localization';
import { LINE_ITEM_TYPES } from '../CertsActionButtonsGroup/constants';
import {
  ACCOUNT_DISPLAY_NAME,
  CHILD_ENTITLEMENT_DISPLAY_NAME,
  ENTITLEMENT_DISPLAY_NAME,
  EO_ACCOUNT_DISPLAY_NAME,
  EO_CHILD_ENTITLEMENT_DISPLAY_NAME,
  EO_ENTITLEMENT_DISPLAY_NAME,
  ROLE_DISPLAY_NAME,
  USER_DISPLAY_NAME,
} from '../CertsReassignModal/constants';

import { invertedMessages } from './messages';

import './CertsReassignPopover.css';

function CertsReassignPopover({
  isCommentPopoverOpen,
  setIsCommentPopoverOpen,
  isMobileXlargeWidth,
  placement,
  trigger,
  onSubmit,
  nodeData,
  getTypeActEntRole,
}) {
  const l = useInvertedLocalize(invertedMessages);

  const handleClose = () => setIsCommentPopoverOpen(false);

  const handleCommentSubmit = () => {
    handleClose();

    return onSubmit();
  };

  /** to get lineitem display name */
  const getSelectedLineItemName = (lineItemType) => {
    let name = '';

    switch (lineItemType) {
      case LINE_ITEM_TYPES.USER:
        name = nodeData[USER_DISPLAY_NAME];
        break;
      case LINE_ITEM_TYPES.ACCOUNT:
        name =
          nodeData[ACCOUNT_DISPLAY_NAME] || nodeData[EO_ACCOUNT_DISPLAY_NAME];
        break;
      case LINE_ITEM_TYPES.ROLE:
        name = nodeData[ROLE_DISPLAY_NAME];
        break;
      case LINE_ITEM_TYPES.ENTITLEMENT:
        name =
          nodeData[ENTITLEMENT_DISPLAY_NAME] ||
          nodeData[EO_ENTITLEMENT_DISPLAY_NAME];
        break;
      case LINE_ITEM_TYPES.CHILD_ENTITLEMENT:
        name =
          nodeData[CHILD_ENTITLEMENT_DISPLAY_NAME] ||
          nodeData[EO_CHILD_ENTITLEMENT_DISPLAY_NAME];
        break;
      default:
        break;
    }

    if (!name) name = 'Not Provided';

    return name;
  };

  const getButtonSize = () => (isMobileXlargeWidth ? 'medium' : 'small');

  const getPopoverContent = () => (
    <Box className='CertsReassignPopover'>
      {isMobileXlargeWidth && (
        <div className='CertsReassignPopover-container-userDetails'>
          <Icon
            kind={l(getTypeActEntRole)} // separate function to get icons
            color='--color-foreground-secondary-bold'
            size='medium'
          />
          <Typography
            kind='h4'
            mobile='h4-d'
            className='CertsReassignPopover-container-userDetailsName'>
            {` ${l(getTypeActEntRole)}: ${getSelectedLineItemName(
              getTypeActEntRole
            )}`}
          </Typography>
        </div>
      )}
      <Typography kind='body1' className='CertsReassignPopover-note'>
        {l(
          'By reassigning this {lineItem}, their associated items will be included. Any approvals or rejections will be cleared upon reassignment.',
          {
            lineItem: l(getTypeActEntRole).toLocaleLowerCase(),
          }
        )}
      </Typography>
      {/* Buttons */}
      <Box tag='div' className='CertsReassignPopover-controls'>
        <Button
          type='button'
          kind='ghost'
          onClick={handleClose}
          size={getButtonSize()}>
          {l('Cancel')}
        </Button>
        <Button
          type='button'
          kind='filled'
          onClick={handleCommentSubmit}
          size={getButtonSize()}
          className='CertsReassignPopover-controls-gotIt'>
          {l('Got it')}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box tag='div' className='CertsReassignPopover-contextWrapper'>
      {isMobileXlargeWidth ? (
        <>
          {trigger}
          <MobileDrawer
            isSwipeEnabled
            isOpen={isCommentPopoverOpen}
            setIsOpen={setIsCommentPopoverOpen}
            content={getPopoverContent()}
            className='CertsReassignPopover'
          />
        </>
      ) : (
        <Popover
          isPortalEnabled
          placement={placement}
          trigger={trigger}
          popoverContent={getPopoverContent()}
          isOpen={isCommentPopoverOpen}
          setIsOpen={setIsCommentPopoverOpen}
        />
      )}
    </Box>
  );
}

CertsReassignPopover.propTypes = {
  isCommentPopoverOpen: PropTypes.bool.isRequired,
  setIsCommentPopoverOpen: PropTypes.func.isRequired,
  isMobileXlargeWidth: PropTypes.bool.isRequired,
  placement: PropTypes.string.isRequired,
  trigger: PropTypes.element.isRequired,
  onSubmit: PropTypes.func.isRequired,
  nodeData: PropTypes.shape(NODE_DATA_MODEL).isRequired,
  getTypeActEntRole: PropTypes.string.isRequired,
};

CertsReassignPopover.defaultProps = {};

export default CertsReassignPopover;
